/**
 * SectionCard — Wrapper visual reutilizado em todas as seções da ContactSidebar (Chat).
 *
 * Padrão unificado com:
 *   - DealModalSidebar v3.0.0
 *   - CustomerModalSidebar v2.0.0
 *   - TaskModalSidebar v5.0.0
 *
 * Tokens fixos:
 *   - Container:  rounded-xl border border-border bg-card shadow-sm p-3
 *   - Título:     text-[10px] font-semibold uppercase tracking-wider text-muted-foreground
 *   - Ícone-âncora à direita: h-3.5 w-3.5 text-muted-foreground (ou ação Pencil/Plus)
 *
 * RELEASE: 2026-05-28 v1.0.0 — Versão inicial extraída para reuso na sidebar do chat.
 */
import React from 'react';
import { cn } from '@/lib/utils';

export interface SectionCardProps {
  /** Título da seção (sempre em UPPERCASE 10px) */
  title: string;
  /** Ícone-âncora opcional à direita (renderizado apenas se action não estiver definido) */
  icon?: React.ComponentType<{ className?: string }>;
  /** Elemento opcional à direita (ex.: botão Pencil, badge). Tem prioridade sobre `icon`. */
  action?: React.ReactNode;
  /** Classe adicional aplicada ao container externo */
  className?: string;
  children: React.ReactNode;
}

/**
 * Wrapper de seção da sidebar — segue o padrão visual dos modais de Negócio/Cliente/Tarefa.
 */
export function SectionCard({
  title,
  icon: Icon,
  action,
  className,
  children,
}: SectionCardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card shadow-sm p-3',
        className,
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        {action ?? (Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />)}
      </div>
      {children}
    </div>
  );
}

/**
 * Linha-item key/value padrão (ícone à esquerda, label, valor à direita com truncate).
 * Use dentro de SectionCard para detalhes simples como "Canal", "Email", "Primeira conversa".
 */
export interface SectionRowProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Valor renderizado à direita. Pode ser string ou ReactNode (badge, picker, etc.) */
  value: React.ReactNode;
  /** Cor opcional para a bolinha 10px à esquerda do valor (ex.: cor da fase) */
  valueDotColor?: string | null;
}

export function SectionRow({ icon: Icon, label, value, valueDotColor }: SectionRowProps) {
  return (
    <div className="flex items-center gap-2 py-1.5 w-full">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <div className="ml-auto min-w-0 flex items-center gap-1.5">
        {valueDotColor && (
          <div
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: valueDotColor }}
          />
        )}
        {typeof value === 'string' ? (
          <span className="text-xs font-medium truncate">{value}</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}
